// client/src/pages/SherlocMediaCampaigns.js (CLIENT READ-ONLY)
// ✅ Client defaults to "All Assigned Advertisers" implicitly (no dropdown).
// ✅ Backend must enforce client scoping in /api/media-campaigns.
// ✅ Clicking a row opens Sherloc Media Planner with that campaign loaded.

import React, { useEffect, useState, useCallback } from "react";
import "./Dashboard.css";
import "./SherlocMediaCampaigns.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import { useNavigate } from "react-router-dom";
import { formatFlightDate } from "../utils/flightDates";
import { reportingDaysUntil } from "../utils/reportingCalendar";
import { getCanonicalCampaignDisplayName } from "../utils/campaignDisplayName";
import { readCampaignStrategyMicrositeAddress } from "../utils/campaignStrategyMicrositeAddress";

// Support CRA (REACT_APP_*) and Vite (VITE_*)
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

const LAST_OPENED_KEY = "sherloc_last_opened_campaign_id";

/* ===============================
   ✅ Normalizers
   =============================== */
const toSafeKey = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const displayLabel = (v) =>
  String(v || "")
    .trim()
    .replaceAll("_", " ");
const planRevisionLabel = (campaign = {}) => {
  const state = String(campaign?.planRevision?.valueState || campaign?.plan_revision?.valueState || "CURRENT_APPROVED_REVISION").toUpperCase();
  return state === "EFFECTIVE_APPROVED_REVISION"
    ? "Effective approved revision"
    : "Current approved revision";
};

const getCampaignStatusPillClassName = (status) => {
  const statusKey = toSafeKey(status) || "unknown";
  const themedModifiers = {
    draft: "sherloc-campaign-status-pill--draft",
    pending: "sherloc-campaign-status-pill--pending",
    reporting_only: "sherloc-campaign-status-pill--reporting-only",
  };
  const themedModifier = themedModifiers[statusKey] || "";

  return [
    "status-badge",
    "sherloc-campaign-status-pill",
    themedModifier,
    `status-${statusKey}`,
  ]
    .filter(Boolean)
    .join(" ");
};

const formatCurrency = (value, opts = {}) => {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "—";

  const { maximumFractionDigits = 0, minimumFractionDigits = 0 } = opts;

  return `$${n.toLocaleString(undefined, {
    maximumFractionDigits,
    minimumFractionDigits,
  })}`;
};

const parseDateSafe = (value) => {
  if (!value) return null;

  if (value instanceof Date && !isNaN(value)) return value;

  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const d = Number(m[3]);
      const dt = new Date(y, mo, d);
      return isNaN(dt) ? null : dt;
    }
  }

  const dt = new Date(value);
  return isNaN(dt) ? null : dt;
};


const getDDaysLifecycleState = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  return normalized === "LIVE" || normalized === "ENDED" ? normalized.toLowerCase() : null;
};

const normalizeStrategyMicrositeHref = (value) => {
  const href = String(value || "").trim();
  if (!href) return "";
  if (/^https?:\/\//i.test(href)) return href;
  if (/^\/(?!\/)/.test(href)) return href;
  if (/^campaign-strategy\/[^?#]+/i.test(href)) return `/${href}`;
  return "";
};

const buildStrategyMicrositePathFromSlug = (value) => {
  const slug = String(value || "").trim().replace(/^\/+|\/+$/g, "");
  if (!slug) return "";

  const normalizedHref = normalizeStrategyMicrositeHref(slug);
  if (normalizedHref) return normalizedHref;
  if (slug.includes("/")) return "";

  return `/campaign-strategy/${encodeURIComponent(slug)}`;
};

const getCampaignStrategyMicrositeHref = (campaign = {}) => {
  // The readable address the backend assigned wins over any older link.
  const readableAddress = readCampaignStrategyMicrositeAddress(campaign);
  if (readableAddress) return readableAddress;

  const strategyPayload =
    campaign?.strategyPayload && typeof campaign.strategyPayload === "object"
      ? campaign.strategyPayload
      : campaign?.strategy_payload && typeof campaign.strategy_payload === "object"
      ? campaign.strategy_payload
      : {};

  const explicitHref = [
    campaign.micrositeUrl,
    campaign.microsite_url,
    campaign.strategyMicrositeUrl,
    campaign.strategy_microsite_url,
    campaign.strategyUrl,
    campaign.strategy_url,
    campaign.publicUrl,
    campaign.public_url,
    campaign.shareUrl,
    campaign.share_url,
    campaign.strategyMicrositePath,
    campaign.strategy_microsite_path,
    strategyPayload.micrositeUrl,
    strategyPayload.microsite_url,
    strategyPayload.strategyMicrositeUrl,
    strategyPayload.strategy_microsite_url,
    strategyPayload.strategyUrl,
    strategyPayload.strategy_url,
    strategyPayload.publicUrl,
    strategyPayload.public_url,
    strategyPayload.shareUrl,
    strategyPayload.share_url,
    strategyPayload.strategyMicrositePath,
    strategyPayload.strategy_microsite_path,
  ]
    .map(normalizeStrategyMicrositeHref)
    .find(Boolean);

  if (explicitHref) return explicitHref;

  return [
    campaign.micrositeSlug,
    campaign.microsite_slug,
    campaign.strategyMicrositeSlug,
    campaign.strategy_microsite_slug,
    campaign.publicSlug,
    campaign.public_slug,
    campaign.slug,
    strategyPayload.micrositeSlug,
    strategyPayload.microsite_slug,
    strategyPayload.strategyMicrositeSlug,
    strategyPayload.strategy_microsite_slug,
    strategyPayload.publicSlug,
    strategyPayload.public_slug,
    strategyPayload.slug,
  ]
    .map(buildStrategyMicrositePathFromSlug)
    .find(Boolean) || "";
};

const SherlocMediaCampaignsClient = () => {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [upcomingOnly, setUpcomingOnly] = useState(true);

  const [now, setNow] = useState(() => new Date());

  const handleLogoClick = () => navigate("/dashboard");

  const handleRowClick = (campaignId) => {
    if (!campaignId) return;

    try {
      localStorage.setItem(LAST_OPENED_KEY, String(campaignId));
    } catch {}

    navigate("/sherloc-media-planner", { state: { campaignId } });
  };

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (!API_BASE_URL) {
        console.error(
          "[SherlocMediaCampaignsClient] API_BASE_URL is not defined. Check env (REACT_APP_BACKEND_URL / VITE_BACKEND_URL)."
        );
        setError("Backend URL is not configured. Please contact your Cphere admin.");
        setCampaigns([]);
        return;
      }

      const token = localStorage.getItem("authToken");

      const params = new URLSearchParams();
      if (upcomingOnly) params.append("upcoming", "1");

      const url = `${API_BASE_URL}/api/media-campaigns?${params.toString()}`;

      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        cache: "no-store",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error("[SherlocMediaCampaignsClient] Failed:", res.status, text);
        setError(`Unable to load campaigns (status ${res.status}). Please try again.`);
        setCampaigns([]);
        return;
      }

      const data = await res.json().catch(() => ({}));
      setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
    } catch (err) {
      console.error("[SherlocMediaCampaignsClient] Network error:", err);
      setError("Network error loading campaigns. Please try again.");
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [upcomingOnly]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => fetchCampaigns(), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchCampaigns]);

  return (
    <div className="smc-page-container">
      <div className="smc-page-content">
        <CphereHeaderLogo
          alt="Powered by Cphere"
          className="smc-poweredby-logo"
          onClick={handleLogoClick}
        />

        <div className="smc-hero-container">
          <div className="smc-hero-pill">
            <span className="smc-hero-pill-label cphere-hero-pill-wordmark">Media</span>
            <span className="smc-hero-pill-tag cphere-hero-pill-tag">Campaigns</span>
          </div>
          <div className="smc-hero-shimmer-bar" />
        </div>

        <div className="mediacampaigns-runs-card mediacampaigns-planned-card">
          <div className="mediacampaigns-runs-header">
            <div className="mediacampaigns-runs-title">Planned Campaigns</div>
            <div className="mediacampaigns-runs-subtitle">
              Updated: {now.toLocaleString()}
            </div>
          </div>

          <div className="mediacampaigns-filters">
            <div className="mediacampaigns-filter-group mediacampaigns-upcoming-toggle">
              <label>Upcoming Only</label>
              <input
                type="checkbox"
                checked={upcomingOnly}
                onChange={(e) => setUpcomingOnly(e.target.checked)}
              />
            </div>
          </div>

          {error && <div className="mediacampaigns-error">{error}</div>}

          {loading ? (
            <div className="mediacampaigns-loading">Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <div className="mediacampaigns-empty">
              No campaigns found for your assigned advertisers.
            </div>
          ) : (
            <div className="mediacampaigns-table-wrapper">
              <div className="mediacampaigns-table-scroll">
                <table className="mediacampaigns-table">
                  <thead>
                    <tr>
                      <th>Advertiser</th>
                      <th>Campaign</th>
                      <th>Status</th>
                      <th>Flight</th>
                      <th>Client-billed Budget</th>
                      <th>D-Days</th>
                      <th>Urgency</th>
                      <th>Strategy</th>
                    </tr>
                  </thead>

                  <tbody>
                    {campaigns.map((c) => {
                      const startDt = parseDateSafe(c.flight_start_date);
                      const endDt = parseDateSafe(c.flight_end_date);

                      let dDaysDisplay =
                        c.business_days_until_start != null
                          ? String(c.business_days_until_start)
                          : "—";
                      let dDaysTitle = "";

                      if (startDt) {
                        const calUntilStart = reportingDaysUntil(startDt, now);

                        if (calUntilStart > 0) {
                          dDaysDisplay = String(calUntilStart);
                          dDaysTitle = `${calUntilStart} days until start (ET)`;
                        } else if (calUntilStart === 0) {
                          dDaysDisplay = "D-DAY";
                          dDaysTitle = "Starts today (ET)";
                        } else {
                          if (endDt) {
                            const calUntilEnd = reportingDaysUntil(endDt, now);
                            if (calUntilEnd >= 0) {
                              dDaysDisplay = "LIVE";
                              dDaysTitle = `Started ${Math.abs(
                                calUntilStart
                              )} days ago • ${calUntilEnd} days until end (ET)`;
                            } else {
                              dDaysDisplay = "ENDED";
                              dDaysTitle = `Ended ${Math.abs(calUntilEnd)} days ago (ET)`;
                            }
                          } else {
                            dDaysDisplay = "LIVE";
                            dDaysTitle = `Started ${Math.abs(calUntilStart)} days ago (ET)`;
                          }
                        }
                      }

                      const computedUrgency =
                        c.launchUrgency || c.launch_urgency || c.urgency || null;

                      const urgencyKey = toSafeKey(computedUrgency) || "none";
                      const urgencyClass = `urgency-badge urgency-${urgencyKey}`;

                      const statusClass = getCampaignStatusPillClassName(c.status);
                      const dDaysLifecycleState = getDDaysLifecycleState(dDaysDisplay);
                      const strategyMicrositeHref = getCampaignStrategyMicrositeHref(c);
                      const campaignDisplayName = getCanonicalCampaignDisplayName(c);

                      return (
                        <tr
                          key={c.id}
                          className="mediacampaigns-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleRowClick(c.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleRowClick(c.id);
                            }
                          }}
                          title="Open in Media Planner"
                        >
                          <td>{c.advertiser_name || "—"}</td>

                          <td className="mediacampaigns-cell-campaign">
                            <div className="mediacampaigns-campaign-name">
                              {campaignDisplayName}
                            </div>
                            {c.objective && (
                              <div className="mediacampaigns-campaign-objective">
                                {c.objective}
                              </div>
                            )}
                          </td>

                          <td>
                            <span className={statusClass}>
                              {c.status ? displayLabel(c.status) : "—"}
                            </span>
                            <div className="mediacampaigns-plan-state">{planRevisionLabel(c)}</div>
                          </td>

                          <td>
                            <div className="mediacampaigns-flight-dates">
                              <span>
                                {formatFlightDate(c.flight_start_date)}
                                {" → "}
                                {formatFlightDate(c.flight_end_date)}
                              </span>
                            </div>
                          </td>

                          <td>
                            {(c.clientBilledBudget ?? c.client_billed_budget ?? c.total_budget) != null
                              ? formatCurrency(c.clientBilledBudget ?? c.client_billed_budget ?? c.total_budget, {
                                  maximumFractionDigits: 0,
                                  minimumFractionDigits: 0,
                                })
                              : "—"}
                          </td>

                          <td className="mc-cell-ddays" title={dDaysTitle}>
                            {dDaysLifecycleState ? (
                              <span
                                className={`mediacampaigns-ddays-pill mediacampaigns-ddays-pill-${dDaysLifecycleState}`}
                              >
                                {dDaysDisplay}
                              </span>
                            ) : (
                              dDaysDisplay
                            )}
                          </td>

                          <td>
                            <span className={urgencyClass}>
                              {computedUrgency ? displayLabel(computedUrgency) : "—"}
                            </span>
                          </td>

                          <td
                            className="mediacampaigns-cell-strategy-action"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {strategyMicrositeHref ? (
                              <a
                                className="mediacampaigns-strategy-button"
                                href={strategyMicrositeHref}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Strategy
                              </a>
                            ) : (
                              <span
                                className="mediacampaigns-strategy-button mediacampaigns-strategy-button--disabled"
                                aria-disabled="true"
                              >
                                Unavailable
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SherlocMediaCampaignsClient;
